import {
  ClearedPlaceholderSessionError,
  InvalidSelectionError,
  type EntityRecord,
  type OccurrenceRecord,
  type PlaceholderSessionOptions,
  type ProtectionResult,
  type ProtectionSelection,
  type RestorationResult,
} from "./types.js";

const TOKEN_LIKE = /\[[A-Z][A-Z0-9_]{0,63}\]/g;
const NONCE = /^[A-Z0-9]{4,16}$/;
const TYPE = /^[A-Z][A-Z0-9_]{0,31}$/;

function randomNonce(): string {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const random = globalThis.crypto?.getRandomValues;
  if (!random) throw new Error("Secure random generation is unavailable.");
  let nonce = "";
  // Rejection sampling avoids modulo bias while producing a fixed-length,
  // valid nonce even when a byte happens to be small.
  while (nonce.length < 8) {
    const bytes = new Uint8Array(16);
    random.call(globalThis.crypto, bytes);
    for (const byte of bytes) {
      if (byte < 252) nonce += alphabet[byte % alphabet.length]!;
      if (nonce.length === 8) break;
    }
  }
  return nonce;
}

function tokenType(type: string): string {
  const normalized = type.normalize("NFKC").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!TYPE.test(normalized)) throw new InvalidSelectionError();
  return normalized;
}

/** Conservative identity normalization is only for stable map identity, never output. */
function identityKey(type: string, value: string): string {
  const normalizedType = tokenType(type);
  if (normalizedType === "EMAIL") return `${normalizedType}\u0000${value.normalize("NFKC").trim().toLocaleLowerCase("und")}`;
  const formatCanonicalTypes = new Set([
    "IBAN",
    "PAYMENT_CARD",
    "SE_PERSON_ID",
    "DK_CPR",
    "LT_PERSON_ID",
    "PHONE",
    "ACCOUNT_NUMBER",
  ]);
  if (formatCanonicalTypes.has(normalizedType)) {
    return `${normalizedType}\u0000${value.normalize("NFKC").replace(/[\s-]/g, "").toLocaleLowerCase("und")}`;
  }
  // Secrets, keys, names, and arbitrary text remain exact. Lossy normalization
  // could otherwise make two different credentials restore as the same value.
  return `${normalizedType}\u0000${value}`;
}

function codePointBefore(text: string, index: number): string | undefined {
  if (index <= 0) return undefined;
  const last = text.charCodeAt(index - 1);
  if (last >= 0xDC00 && last <= 0xDFFF && index >= 2) {
    const first = text.charCodeAt(index - 2);
    if (first >= 0xD800 && first <= 0xDBFF) return text.slice(index - 2, index);
  }
  return text.slice(index - 1, index);
}

function codePointAt(text: string, index: number): string | undefined {
  if (index < 0 || index >= text.length) return undefined;
  const value = text.codePointAt(index);
  return value === undefined ? undefined : String.fromCodePoint(value);
}

function isWordCodePoint(value: string | undefined): boolean {
  return value !== undefined && /^[\p{L}\p{N}_]$/u.test(value);
}

function isTokenBoundary(text: string, start: number, end: number): boolean {
  return !(
    isWordCodePoint(codePointBefore(text, start)) ||
    isWordCodePoint(codePointAt(text, end))
  );
}

export interface PlaceholderSession {
  readonly nonce: string;
  protect(text: string, selections: readonly ProtectionSelection[]): ProtectionResult;
  restore(text: string): RestorationResult;
  /** Forgets every extension-accessible mapping reference in this session. */
  clear(): void;
  isCleared(): boolean;
  entities(): readonly EntityRecord[];
  occurrences(): readonly OccurrenceRecord[];
}

export function createPlaceholderSession(options: PlaceholderSessionOptions = {}): PlaceholderSession {
  const nonce = options.nonce ?? randomNonce();
  if (!NONCE.test(nonce)) throw new InvalidSelectionError();
  const reservedTokens = new Set(options.reservedTokens ?? []);
  const entitiesById = new Map<string, EntityRecord>();
  const entityIdByKey = new Map<string, string>();
  const entityIdByToken = new Map<string, string>();
  const occurrenceRecords: OccurrenceRecord[] = [];
  const counters = new Map<string, number>();
  let entityCounter = 0;
  let cleared = false;

  const ensureLive = (): void => {
    if (cleared) throw new ClearedPlaceholderSessionError();
  };

  const reserveTokensIn = (text: string): void => {
    for (const match of text.matchAll(TOKEN_LIKE)) reservedTokens.add(match[0]);
  };

  const allocateToken = (type: string): string => {
    const prefix = tokenType(type);
    let number = counters.get(prefix) ?? 0;
    let token: string;
    do {
      number += 1;
      token = `[${prefix}_${nonce}_${String(number).padStart(3, "0")}]`;
    } while (reservedTokens.has(token) || entityIdByToken.has(token));
    counters.set(prefix, number);
    reservedTokens.add(token);
    return token;
  };

  const entityFor = (value: string, selection: ProtectionSelection): EntityRecord => {
    if (selection.entityId) {
      const explicit = entitiesById.get(selection.entityId);
      if (!explicit) throw new InvalidSelectionError();
      return explicit;
    }
    const type = tokenType(selection.type);
    const canonicalValue = selection.canonicalValue ?? value;
    const key = identityKey(type, canonicalValue);
    const existingId = entityIdByKey.get(key);
    if (existingId) return entitiesById.get(existingId)!;
    entityCounter += 1;
    const entity: EntityRecord = {
      entityId: `entity_${nonce}_${String(entityCounter).padStart(3, "0")}`,
      type,
      canonicalValue,
      token: allocateToken(type),
    };
    entitiesById.set(entity.entityId, entity);
    entityIdByKey.set(key, entity.entityId);
    entityIdByToken.set(entity.token, entity.entityId);
    return entity;
  };

  return {
    nonce,
    protect(text, selections) {
      ensureLive();
      reserveTokensIn(text);
      const ordered = [...selections].sort((left, right) => left.startUtf16 - right.startUtf16 || left.endUtf16 - right.endUtf16);
      let previousEnd = 0;
      for (const selection of ordered) {
        if (!Number.isInteger(selection.startUtf16) || !Number.isInteger(selection.endUtf16)
          || selection.startUtf16 < 0 || selection.endUtf16 <= selection.startUtf16
          || selection.endUtf16 > text.length || selection.startUtf16 < previousEnd) throw new InvalidSelectionError();
        previousEnd = selection.endUtf16;
      }
      const currentOccurrences: OccurrenceRecord[] = [];
      let protectedText = text;
      // Right-to-left replacement preserves every original selection offset.
      for (let index = ordered.length - 1; index >= 0; index -= 1) {
        const selection = ordered[index]!;
        const originalValue = text.slice(selection.startUtf16, selection.endUtf16);
        const entity = entityFor(originalValue, selection);
        protectedText = `${protectedText.slice(0, selection.startUtf16)}${entity.token}${protectedText.slice(selection.endUtf16)}`;
        const occurrence = { entityId: entity.entityId, originalValue, startUtf16: selection.startUtf16, endUtf16: selection.endUtf16 };
        currentOccurrences.unshift(occurrence);
      }
      // `protect` is also the live preview operation. Keep only the latest
      // review's occurrences so checkbox toggles are idempotent and cannot
      // retain unbounded duplicate copies of sensitive values.
      occurrenceRecords.splice(0, occurrenceRecords.length, ...currentOccurrences);
      return { text: protectedText, entities: [...entitiesById.values()], occurrences: currentOccurrences };
    },
    restore(text) {
      ensureLive();
      const tokenAlternation = [...entityIdByToken.keys()].sort((left, right) => right.length - left.length).map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
      if (!tokenAlternation) return { text, replacements: 0 };
      const expression = new RegExp(`(?:${tokenAlternation})`, "g");
      let replacements = 0;
      const restored = text.replace(expression, (token: string, offset: number, source: string) => {
        if (!isTokenBoundary(source, offset, offset + token.length)) return token;
        const entityId = entityIdByToken.get(token);
        if (!entityId) return token;
        replacements += 1;
        return entitiesById.get(entityId)!.canonicalValue;
      });
      return { text: restored, replacements };
    },
    clear() {
      // Clear all reachable local references. JavaScript/browser memory cannot
      // promise physical zeroization, so callers must not make that claim.
      entitiesById.clear();
      entityIdByKey.clear();
      entityIdByToken.clear();
      occurrenceRecords.length = 0;
      reservedTokens.clear();
      counters.clear();
      cleared = true;
    },
    isCleared: () => cleared,
    entities: () => {
      ensureLive();
      return [...entitiesById.values()];
    },
    occurrences: () => {
      ensureLive();
      return [...occurrenceRecords];
    },
  };
}
